import { create } from "zustand";
import { persist } from "zustand/middleware";

import { NOTE_CATEGORIES, type NoteCategory, type NoteSort } from "@/lib/api";

/** How many cards a page of the feed fetches. Eight rows at the widest 3
 * column layout (HomeTab caps at 3 across), so the first paint fills the
 * viewport with room to scroll, without over-fetching a large vault. */
export const PAGE_SIZE = 24;

interface HomeState {
  /** The live search box value. NOT persisted — landing on Home with a stale
   * query from last week would look like a broken, near-empty vault. */
  query: string;
  sort: NoteSort;
  /** Categories to INCLUDE. Empty means all four (the server's own default),
   * which is also what "every checkbox ticked" collapses to. */
  categories: NoteCategory[];
  /** A subfolder to narrow to, scoped to a category. Both or neither. */
  folderCategory: NoteCategory | null;
  folder: string;
  setQuery: (q: string) => void;
  setSort: (s: NoteSort) => void;
  toggleCategory: (c: NoteCategory) => void;
  setCategories: (c: NoteCategory[]) => void;
  setFolder: (category: NoteCategory | null, path: string) => void;
  clearFilters: () => void;
  /** How many facets are actively narrowing the feed — the Filter button's
   * count badge, and what tells the user why they are seeing fewer notes. */
  activeFilterCount: () => number;
}

const ALL = NOTE_CATEGORIES.length;

export const useHome = create<HomeState>()(
  persist(
    (set, get) => ({
      query: "",
      sort: "recent",
      categories: [],
      folderCategory: null,
      folder: "",
      setQuery: (query) => set({ query }),
      setSort: (sort) => set({ sort }),
      toggleCategory: (category) =>
        set((s) => {
          // An empty list means "all", so the first un-tick has to start from
          // the full set rather than from nothing — otherwise un-ticking one
          // category would leave exactly one selected.
          const current = s.categories.length ? s.categories : [...NOTE_CATEGORIES];
          const next = current.includes(category)
            ? current.filter((c) => c !== category)
            : [...current, category];
          // Un-ticking the last one is almost certainly a mis-click, and an
          // empty homepage with no way back is a worse outcome than ignoring it.
          if (next.length === 0) return s;
          const all = next.length === ALL;
          // A subfolder belongs to exactly ONE category — "Research" under
          // Markdown and "Research" under Ingested are different folders. The
          // API's `folder` facet has no category of its own, so it would match
          // BOTH the moment a second category is selected. Rather than silently
          // widening what the user asked for, the folder narrowing is dropped
          // whenever the selection stops being exactly its own category.
          const keepsFolder =
            s.folderCategory !== null && next.length === 1 && next[0] === s.folderCategory;
          return {
            categories: all ? [] : next,
            ...(s.folderCategory !== null && !keepsFolder
              ? { folderCategory: null, folder: "" }
              : null),
          };
        }),
      setCategories: (categories) =>
        set({ categories: categories.length === ALL ? [] : categories }),
      setFolder: (folderCategory, folder) =>
        set(
          folderCategory && folder
            ? {
                folderCategory,
                folder,
                // Narrowing to a folder implies its category — otherwise the
                // folder filter and the category filter can contradict.
                categories: [folderCategory],
              }
            : { folderCategory: null, folder: "" },
        ),
      clearFilters: () =>
        set({ sort: "recent", categories: [], folderCategory: null, folder: "" }),
      activeFilterCount: () => {
        const s = get();
        return (
          (s.categories.length > 0 && s.categories.length < ALL ? 1 : 0) +
          (s.folder ? 1 : 0) +
          (s.sort !== "recent" ? 1 : 0)
        );
      },
    }),
    {
      name: "graphmind.home.filters",
      partialize: ({ query: _query, ...rest }) => rest,
    },
  ),
);
